import { Grid } from '@paintscout/ui'
import {
  EstimatorSelect,
  DateRangePicker,
  TagSelection,
  KeypadInputField,
  DropdownSelect,
  useClientOptions,
  useUserPermissions
} from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import { getFeatures, getObjectLabels, getQuoteTypeOptions, getTags } from '@paintscout/util/builder'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util/users'
import type { InFilter, RangeFilter, ValueFilter } from 'json-to-lucene'
import React from 'react'
import moment from 'moment'
import { handleFilterChange, handleRangeFilterChange } from '@paintscout/util/util'
import type { QuoteTag } from 'paintscout'
import { useFlags } from 'shared/components/common/FlagsProvider'
import QuoteStatusSelection from '../QuoteStatusSelection'

const FILTER_TOTALS_AFTER_TAX = 'totals.after_tax'

export interface QuoteSearchFiltersProps {
  invoice: boolean
  filters: ValueFilter[]
  onChange?: (filters: ValueFilter[]) => any
}

export default function QuoteSearchFilters({ invoice, filters, onChange }: QuoteSearchFiltersProps) {
  const { options } = useClientOptions()
  const { permissionToViewEstimator } = useUserPermissions()

  const { quote: quoteLabel, currency } = getObjectLabels({ options })

  const { crm: crmFlag, canDeactivateSettingsItems } = useFlags()
  const features = getFeatures({ options })
  const tagsEnabled = !!(features.quotes?.tags?.enabled || crmFlag)

  const archivedOptions: DropdownSelectOption[] = [
    { label: 'No Archived', value: ['false'] },
    { label: 'Include Archived', value: ['true', 'false'] },
    { label: 'Only Archived', value: ['true'] }
  ]

  const typeOptions = getQuoteTypeOptions({ options, hideAll: false, canDeactivate: !!canDeactivateSettingsItems }).map(
    (type) => ({
      value: type.key,
      label: type.label
    })
  )

  const filteredTags = getTags({
    options,
    keys: filters
      .filter((f) => f.id.startsWith('tags'))
      .map((f) => f.value)
      .flat()
  })

  const handleTagsChange = (newTags: QuoteTag[]) => {
    const tagFilter: ValueFilter = newTags.reduce(
      (acc, tag) => {
        return {
          ...acc,
          value: [...acc.value, tag.name]
        }
      },
      {
        id: `tags`,
        type: 'in',
        value: [],
        operator: 'AND'
      }
    )

    if (onChange) {
      onChange([...filters.filter(({ id }) => id !== 'tags'), tagFilter])
    }
  }

  const dateFilter = filters.find((filter) => filter.id === 'quote_date')
  const statusFilter = filters.find((filter) => filter.id === 'status.value')
  const ownerFilter = filters.find(({ id }) => id === 'owner.id') as InFilter
  const taxFilter = filters.find(({ id }) => id === FILTER_TOTALS_AFTER_TAX) as RangeFilter
  const archivedFilter = filters.find(({ id }) => id === 'archived')

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={12} lg={tagsEnabled ? 3 : 4}>
        <DateRangePicker
          startDate={dateFilter?.value[0] ? moment(dateFilter.value[0]) : null}
          endDate={dateFilter?.value[1] ? moment(dateFilter.value[1]) : null}
          onConfirm={(startDate, endDate) => {
            handleRangeFilterChange({
              id: 'quote_date',
              start: startDate ? startDate.set('hour', 0).unix() * 1000 : null,
              end: endDate ? endDate.set('hour', 23).unix() * 1000 : null,
              filters,
              onChange
            })
          }}
          PopperProps={{
            placement: 'bottom-end'
          }}
        />
      </Grid>
      <Grid item xs={12} lg={tagsEnabled ? 3 : 4}>
        <DropdownSelect
          label={`${invoice ? 'Invoice' : quoteLabel.value} Type`}
          variant="multi"
          options={typeOptions}
          searchable
          hideCloseButton
          placeholder={{
            selectPlaceholder: `Any ${invoice ? 'invoice' : quoteLabel.value.toLowerCase()} type`,
            plural: `Types`
          }}
          value={typeOptions.filter((opt) =>
            (filters.find((filter) => filter.id === 'int_ext') as InFilter)?.value.find((v) => v === opt.value)
          )}
          onChange={(selectedOptions) => {
            handleFilterChange(
              {
                id: 'int_ext',
                type: 'in',
                value: selectedOptions.map((o) => o.value)
              },
              filters,
              onChange
            )
          }}
        />
      </Grid>
      <Grid item xs={12} lg={tagsEnabled ? 3 : 4}>
        <QuoteStatusSelection
          name="status"
          label={`${invoice ? 'Invoice' : quoteLabel.value} Status`}
          invoice={invoice}
          variant="multi"
          placeholder={{
            selectPlaceholder: `Any ${invoice ? 'invoice' : quoteLabel.value.toLowerCase()} status`,
            plural: `Statuses`
          }}
          value={statusFilter?.value as string[]}
          onChange={(options) => {
            handleFilterChange(
              {
                id: 'status.value',
                type: 'in',
                value: options.map((o) => o.value)
              },
              filters,
              onChange
            )
          }}
          fullWidth
        />
      </Grid>
      {permissionToViewEstimator && (
        <Grid item xs={12} lg={tagsEnabled ? 3 : 4}>
          <EstimatorSelect
            name="estimator"
            label="Estimator"
            searchable
            variant="multi"
            value={(typeof ownerFilter?.value === 'string' ? [ownerFilter?.value] : ownerFilter?.value)?.map((val) =>
              addAuth0Prefix(val as string)
            )}
            roles={['sales', 'admin']}
            placeholder={{
              selectPlaceholder: 'Any estimator',
              plural: 'Estimators'
            }}
            onChange={(options: DropdownSelectOption[]) => {
              handleFilterChange(
                {
                  id: 'owner.id',
                  type: 'in',
                  value: options.map((option) => removeAuth0Prefix(option.value))
                },
                filters,
                onChange
              )
            }}
            fullWidth
          />
        </Grid>
      )}

      <Grid container alignItems="flex-end" item xs={12} lg={tagsEnabled ? 3 : 4}>
        <Grid item xs={6} style={{ paddingRight: 4 }}>
          <KeypadInputField
            label={`Amount (${currency.symbol})`}
            margin="none"
            placeholder="Min"
            format="dimension"
            fullWidth
            allowClear
            value={taxFilter?.value?.[0] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_TOTALS_AFTER_TAX, start: value, filters, onChange })
            }}
          />
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: 4 }}>
          <KeypadInputField
            margin="none"
            flipX
            placeholder="Max"
            format="dimension"
            value={taxFilter?.value?.[1] ?? ''}
            fullWidth
            allowClear
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: FILTER_TOTALS_AFTER_TAX, end: value, filters, onChange })
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} lg={tagsEnabled ? 3 : 4}>
        <DropdownSelect
          onChange={(option: DropdownSelectOption) => {
            handleFilterChange(
              {
                id: 'archived',
                type: 'in',
                value: option.value
              },
              filters,
              onChange
            )
          }}
          label={'Archived'}
          fullWidth
          variant="single"
          value={
            archivedOptions.find((option) => {
              if (!archivedFilter) {
                return false
              }

              return option.value[0] === archivedFilter?.value[0] && option.value[1] === archivedFilter?.value[1]
            }) || null
          }
          options={archivedOptions}
        />
      </Grid>
      {tagsEnabled && (
        <Grid item xs={12} lg={6}>
          <TagSelection label="Tags" appliedTags={filteredTags} fullWidth handleChange={handleTagsChange} />
        </Grid>
      )}
    </Grid>
  )
}
